/**
 * Fail2banReactionToggle — JEDEN přepínač mostu fail2ban → self-defence,
 * sdílený všude, kde se zapíná veřejný přístup, i v modulu Self-defence.
 * Filip 2026-10-07: „jde udělat jeden widget a ten dát do obou“ a hned nato
 * „ten modal je jiný než ty pod ním, celé je to nekonzistentní“ → komponenta
 * NEMÁ vlastní vzhled; kreslí se přesně jako řádky, mezi kterými stojí:
 *   - variant 'service-row'  = řádek služby na kartě Veřejný přístup (NodeServicesCard)
 *   - variant 'publish-row'  = řádek SSO / API block v PublishPanel
 *   - Fail2banReactionCard   = standardní Card s akcemi v hlavičce (Zabezpečení,
 *                              Self-defence, widget self-defence.reaction)
 *
 * Stav = self-defence config `fail2ban_feed` (desired), skutečnost =
 * fail2ban-bridge.health (actual). Přepnutí = self-defence.config set, které
 * most skutečně nainstaluje/odebere.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Ban, ShieldAlert, ShieldCheck } from 'lucide-react';
import { mcpCall } from '../../lib/mcp';
import { AsyncStatusToggle } from './AsyncStatusToggle';
import { Button } from './Button';
import { Card, CardContent, CardHeader, CardTitle } from './Card';

type UtmStatus = { ok: boolean; config: Record<string, unknown> };
type Health = { ok: boolean; error?: string };

function useReaction() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data: status } = useQuery({
    queryKey: ['self-defence-status'],
    queryFn: () => mcpCall<UtmStatus>('self-defence.utm_status', {}),
    refetchInterval: 60_000,
  });
  const { data: health } = useQuery({
    queryKey: ['fail2ban-bridge-health'],
    queryFn: () => mcpCall<Health>('fail2ban-bridge.health', {}),
    refetchInterval: 60_000,
  });
  const toggle = useMutation({
    mutationFn: async (value: boolean) => {
      const r = await mcpCall<{ ok: boolean; error?: string }>('self-defence.config', {
        action: 'set',
        key: 'fail2ban_feed',
        value,
      });
      if (!r.ok) throw new Error(r.error ?? t('selfDefence.reactionFailed'));
      return r;
    },
    onSuccess: (_r, value) => {
      toast.success(value ? t('selfDefence.reactionEnabled') : t('selfDefence.reactionDisabled'));
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['self-defence-status'] });
      qc.invalidateQueries({ queryKey: ['fail2ban-bridge-health'] });
    },
  });
  return { enabled: Boolean(status?.config?.fail2ban_feed), health, toggle };
}

/** Samotný přepínač — stejný AsyncStatusToggle jako SSO / Veřejně. */
function ReactionSwitch({ label }: { label?: React.ReactNode }) {
  const { t } = useTranslation();
  const { enabled, health, toggle } = useReaction();
  return (
    <AsyncStatusToggle
      value={enabled}
      actual={health?.ok}
      pending={toggle.isPending}
      onToggle={(v) => toggle.mutateAsync(v)}
      onError={(e) => toast.error(e instanceof Error ? e.message : String(e))}
      renderIcon={(on) => (
        <ShieldAlert size={12} className={on ? 'text-emerald-400' : 'text-gray-600'} />
      )}
      renderIdleBadge={() =>
        health
          ? (
            <span className={`text-[10px] ${health.ok ? 'text-emerald-400' : 'text-amber-400'}`}>
              {health.ok ? t('selfDefence.reactionHealthy') : t('selfDefence.reactionMissing')}
            </span>
          )
          : null}
      label={label}
    />
  );
}

export interface Fail2banReactionToggleProps {
  variant: 'service-row' | 'publish-row';
}

export function Fail2banReactionToggle({ variant }: Fail2banReactionToggleProps) {
  const { t } = useTranslation();
  if (variant === 'publish-row') {
    // 1:1 jako řádek „Keycloak SSO z venku“ / „Blokovat API z venku“ v PublishPanel.
    return (
      <div className='px-3 py-2 rounded-xl border border-gray-800 bg-gray-900/30'>
        <ReactionSwitch
          label={
            <div className='flex flex-col'>
              <span className='text-[11px] text-gray-300'>{t('selfDefence.reactionToggle')}</span>
              <span className='text-[10px] text-gray-600'>{t('selfDefence.reactionHint')}</span>
            </div>
          }
        />
      </div>
    );
  }
  // 1:1 jako řádek služby (UI / API / MCP) na kartě Veřejný přístup.
  return (
    <div className='px-4 py-3 rounded-xl bg-gray-900/40 border border-gray-800'>
      <div className='flex items-start gap-3'>
        <div className='flex-1 min-w-0'>
          <div className='flex items-center gap-2 flex-wrap'>
            <span className='text-sm font-semibold text-white'>{t('selfDefence.reactionTitle')}</span>
          </div>
          <p className='text-[11px] text-gray-500 mt-0.5'>{t('selfDefence.reactionHint')}</p>
        </div>
        <div className='shrink-0 min-w-[160px]'>
          <ReactionSwitch label={t('selfDefence.reactionToggle')} />
        </div>
      </div>
    </div>
  );
}

export interface Fail2banReactionCardProps {
  /** Přepnutí záložky uvnitř Self-defence; bez něj odkazy navigují na stránku modulu. */
  onOpenList?: (list: 'blacklist' | 'whitelist') => void;
}

/** Standardní karta s akcemi v hlavičce — Zabezpečení, Self-defence, widget. */
export function Fail2banReactionCard({ onOpenList }: Fail2banReactionCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const openList = (list: 'blacklist' | 'whitelist') =>
    onOpenList ? onOpenList(list) : navigate(`/modules/self-defence?tab=${list}`);
  return (
    <Card>
      <CardHeader>
        <div className='flex items-center justify-between gap-2 flex-wrap'>
          <CardTitle icon={ShieldAlert}>{t('selfDefence.reactionTitle')}</CardTitle>
          <div className='flex items-center gap-2'>
            <Button variant='secondary' size='sm' icon={<Ban size={12} />} onClick={() => openList('blacklist')}>
              {t('selfDefence.openBlacklist')}
            </Button>
            <Button variant='secondary' size='sm' icon={<ShieldCheck size={12} />} onClick={() => openList('whitelist')}>
              {t('selfDefence.openWhitelist')}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Fail2banReactionToggle variant='service-row' />
      </CardContent>
    </Card>
  );
}
